import { type FC, useCallback } from "react";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { TrackLocations } from "@/hooks/use-segment";
import { pluralize } from "@/utils/strings";

export type RemoveMemberConfirmDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    memberNames: string[];
    isLoading?: boolean;
    onConfirm: () => void | Promise<void>;
};

export const RemoveMemberConfirmDialog: FC<RemoveMemberConfirmDialogProps> = ({
    open,
    onOpenChange,
    memberNames,
    isLoading = false,
    onConfirm,
}) => {
    const handleConfirm = useCallback(async () => {
        await onConfirm();
    }, [onConfirm]);

    const memberCount = memberNames.length;
    const isBulk = memberCount > 1;

    // Format member names for display
    const displayNames = memberNames.slice(0, 3);
    if (memberCount > 3) {
        displayNames.push(`${memberCount - 3} more`);
    }
    const formattedNames = displayNames.join(", ");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-track-location={TrackLocations.SettingsGroupDetailsRemove}>
                <DialogHeader>
                    <DialogTitle>{isBulk ? `Remove ${pluralize(memberCount, "member")}` : "Remove member"}</DialogTitle>
                    <DialogDescription>
                        {isBulk ? (
                            <>
                                Are you sure you want to remove {memberCount} {pluralize(memberCount, "member")} from
                                this group?
                                {formattedNames && <span className="mt-2 block font-medium">{formattedNames}</span>}
                            </>
                        ) : (
                            <>
                                Are you sure you want to remove <span>{memberNames[0]}</span> from this group?
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isLoading}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        loading={isLoading}
                        disabled={isLoading}
                        data-tracking-id="remove"
                    >
                        Remove
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
